import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { SchoolAssignmentService } from './services/assignment.school.service';
import { SchoolAttendanceService } from './services/attendance.school.service';
import { authGuard } from './services/auth.guard';
import { SchoolAwardService } from './services/award.school.service';
import { SchoolExamService } from './services/exam.school.service';
import { SchoolGradeLessonService } from './services/grade-lesson.school.service';
import { SchoolGradeService } from './services/grade.school.service';
import { groupAccountingGuard } from './services/group-accounting.guard';
import { groupAdminGuard } from './services/group-admin.guard';
import { CAMPAIGN_ROUTE } from './views/group/campaigns/campaign.routes';
import { groupWriteAccessGuard } from './services/group-write.guard';
import { SchoolLessonService } from './services/lesson.school.service';
import { SchoolMemberService } from './services/member.school.service';
import { schoolAccessGuard } from './services/school-access.guard';
import { SchoolSessionService } from './services/session.school.service';
import { SchoolAssignmentEffects } from './state/effects/assignment.school.effects';
import { SchoolAttendanceEffects } from './state/effects/attendance.school.effects';
import { SchoolAwardEffects } from './state/effects/award.school.effects';
import { SchoolExamEffects } from './state/effects/exam.school.effects';
import { SchoolGradeLessonEffects } from './state/effects/grade-lesson.school.effects';
import { SchoolGradeEffects } from './state/effects/grade.school.effects';
import { SchoolLessonEffects } from './state/effects/lesson.school.effects';
import { SchoolMemberEffects } from './state/effects/member.school.effects';
import { SchoolSessionEffects } from './state/effects/session.school.effects';
import { TeacherEffects } from './state/effects/teacher.school.effects';
import { ListAlbumsComponent } from './views/albums/list-albums/list-albums.component';
import { ViewAlbumComponent } from './views/albums/view-album/view-album.component';
import { LoginComponent } from './views/auth/login/login.component';
import { JoinOrganizationComponent } from './views/management/join-organization/join-organization.component';
import { OrgRequestComponent } from './views/management/org-request/org-request.component';
import { OrgRequestsReviewComponent } from './views/platform/org-requests-review/org-requests-review.component';
import { HowToComponent } from './views/docs/how-to/how-to.component';
import { AccountingComponent } from './views/group/accounting/accounting.component';
import { ChurchAffiliationComponent } from './views/group/church-affiliation/church-affiliation.component';
import { UploadReceiptsComponent } from './views/group/accounting/upload-receipts/upload-receipts.component';
import { CreateGroupComponent } from './views/group/create-group/create-group.component';
import { EditGroupComponent } from './views/group/edit-group/edit-group.component';
import { CreateEventComponent } from './views/group/events/create-event/create-event.component';
import { DetailEventComponent } from './views/group/events/detail-event/detail-event.component';
import { EditEventComponent } from './views/group/events/edit-event/edit-event.component';
import { ListEventComponent } from './views/group/events/list-event/list-event.component';
import { FundingComponent } from './views/group/funding/funding.component';
import { GivingComponent } from './views/group/giving/giving.component';
import { GroupComponent } from './views/group/group/group.component';
import { HomeComponent } from './views/group/home/home.component';
import { ListGroupsComponent } from './views/group/list-groups/list-groups.component';
import { SearchCommunitiesComponent } from './views/group/search-communities/search-communities.component';
import { CreateMemberComponent } from './views/group/members/create-member/create-member.component';
import { DetailMemberComponent } from './views/group/members/detail-member/detail-member.component';
import { EditMemberComponent } from './views/group/members/edit-member/edit-member.component';
import { ListMemberComponent } from './views/group/members/list-member/list-member.component';
import { CreatePrayerComponent } from './views/group/prayers/create-prayer/create-prayer.component';
import { DetailPrayerComponent } from './views/group/prayers/detail-prayer/detail-prayer.component';
import { EditPrayerComponent } from './views/group/prayers/edit-prayer/edit-prayer.component';
import { ListPrayerComponent } from './views/group/prayers/list-prayer/list-prayer.component';
import { LoadPrayerComponent } from './views/group/prayers/load-prayer/load-prayer.component';
import { AdminComponent } from './views/layouts/admin/admin.component';
import { AuthComponent } from './views/layouts/auth/auth.component';
import { EventInviteComponent } from './views/links/invite/event-invite/event-invite.component';
import { GroupInviteComponent } from './views/links/invite/group-invite/group-invite.component';
import { OrgInviteComponent } from './views/links/invite/org-invite/org-invite.component';
import { LinkComponent } from './views/links/link/link.component';
import { CreateArticleComponent } from './views/media/articles/create-article/create-article.component';
import { EditArticleComponent } from './views/media/articles/edit-article/edit-article.component';
import { MyArticleComponent } from './views/media/articles/my-article/my-article.component';
import { MyArticlesComponent } from './views/media/articles/my-articles/my-articles.component';
import { ChannelComponent } from './views/media/channel/channel.component';
import { CreateChannelComponent } from './views/media/channel/create-channel/create-channel.component';
import { EditChannelComponent } from './views/media/channel/edit-channel/edit-channel.component';
import { ViewChannelComponent } from './views/media/channel/view-channel/view-channel.component';
import { CreateVideoComponent } from './views/media/videos/create-video/create-video.component';
import { EditVideoComponent } from './views/media/videos/edit-video/edit-video.component';
import { MyVideoComponent } from './views/media/videos/my-video/my-video.component';
import { MyVideosComponent } from './views/media/videos/my-videos/my-videos.component';
import { NotFoundComponent } from './views/not-found/not-found.component';
import { ResultComponent } from './views/payments/result/result.component';
import { EditProfileComponent } from './views/profile/edit-profile/edit-profile.component';
import { MyCertificatesComponent } from './views/profile/my-certificates/my-certificates.component';
import { MyGivingComponent } from './views/profile/my-giving/my-giving.component';
import { ProfileComponent } from './views/profile/profile.component';
import { ProfileSettingsComponent } from './views/profile/settings/profile-settings.component';
import { ViewProfileComponent } from './views/profile/view-profile/view-profile.component';
import { CreateQuestionsComponent } from './views/quizzes/create-questions/create-questions.component';
import { CreateQuizComponent } from './views/quizzes/create-quiz/create-quiz.component';
import { LiveParticipantQuizComponent } from './views/quizzes/live-participant-quiz/live-participant-quiz.component';
import { LiveQuizRoomComponent } from './views/quizzes/live-quiz-room/live-quiz-room.component';
import { QuestionBankComponent } from './views/quizzes/question-bank/question-bank.component';
import { QuizAnalyticsComponent } from './views/quizzes/quiz-detail/quiz-analytics.component';
import { QuizDetailComponent } from './views/quizzes/quiz-detail/quiz-detail.component';
import { QuizInfoComponent } from './views/quizzes/quiz-detail/quiz-info.component';
import { QuizEditComponent } from './views/quizzes/quiz-edit/quiz-edit.component';
import { QuizHomeComponent } from './views/quizzes/quiz-home/quiz-home.component';
import { QuizReportComponent } from './views/quizzes/quiz-report/quiz-report.component';
import { ActiveQuizComponent } from './views/quizzes/schedule/active-quiz/active-quiz.component';
import { ScheduleQuizComponent } from './views/quizzes/schedule/schedule-quiz/schedule-quiz.component';
import { AddAssignmentComponent } from './views/school/assignments/add-assignment/add-assignment.component';
import { DetailAssignmentComponent } from './views/school/assignments/detail-assignment/detail-assignment.component';
import { EditAssignmentNotesComponent } from './views/school/assignments/edit-assignment-notes/edit-assignment-notes.component';
import { EditAssignmentComponent } from './views/school/assignments/edit-assignment/edit-assignment.component';
import { ListAssignmentsComponent } from './views/school/assignments/list-assignments/list-assignments.component';
import { CreateAttendanceComponent } from './views/school/attendances/create-attendance/create-attendance.component';
import { DetailAttendanceComponent } from './views/school/attendances/detail-attendance/detail-attendance.component';
import { EditAttendanceComponent } from './views/school/attendances/edit-attendance/edit-attendance.component';
import { ListAttendanceComponent } from './views/school/attendances/list-attendance/list-attendance.component';
import { DashboardComponent } from './views/school/dashboard/dashboard.component';
import { EventsCalendarComponent } from './views/school/events-calendar/events-calendar.component';
import { CreateNotesComponent } from './views/school/grade-lessons/create-notes/create-notes.component';
import { EditNotesComponent } from './views/school/grade-lessons/edit-notes/edit-notes.component';
import { ListGradeLessonsComponent } from './views/school/grade-lessons/list-grade-lessons/list-grade-lessons.component';
import { ViewLessonComponent } from './views/school/grade-lessons/view-lesson/view-lesson.component';
import { ViewNotesComponent } from './views/school/grade-lessons/view-notes/view-notes.component';
import { DetailGradeComponent } from './views/school/grades/detail-grade/detail-grade.component';
import { ListGradesComponent } from './views/school/grades/list-grades/list-grades.component';
import { AddLessonComponent } from './views/school/lessons/add-lesson/add-lesson.component';
import { DetailLessonComponent } from './views/school/lessons/detail-lesson/detail-lesson.component';
import { ListLessonsComponent } from './views/school/lessons/list-lessons/list-lessons.component';
import { UpdateLessonComponent } from './views/school/lessons/update-lesson/update-lesson.component';
import { DetailFacultyComponent } from './views/school/members/detail-faculty/detail-faculty.component';
import { DetailParentComponent } from './views/school/members/detail-parent/detail-parent.component';
import { DetailStudentComponent } from './views/school/members/detail-student/detail-student.component';
import { EditChildProfileComponent } from './views/school/members/edit-child-profile/edit-child-profile.component';
import { ListFacultyComponent } from './views/school/members/list-faculty/list-faculty.component';
import { ListSchoolMembersComponent } from './views/school/members/list-members/list-members.component';
import { ListStudentsComponent } from './views/school/members/list-students/list-students.component';
import { ListUnassignedComponent } from './views/school/members/list-unassigned/list-unassigned.component';
import { DlgViewNoticeComponent } from './views/school/notices/dlg-view-notice/dlg-view-notice.component';
import { NoticeboardComponent } from './views/school/notices/noticeboard/noticeboard.component';
import { UpsertNoticeComponent } from './views/school/notices/upsert-notice/upsert-notice.component';
import { SubscriptionComponent } from './views/school/payments/subscription/subscription.component';
import { ViewAccountComponent } from './views/school/payments/view-account/view-account.component';
import { CreditTransactionsComponent } from './views/school/payments/credit-transactions/credit-transactions.component';
import { DetailSessionComponent } from './views/school/sessions/detail-session/detail-session.component';
import { ListSessionsComponent } from './views/school/sessions/list-sessions/list-sessions.component';
import { SchoolSettingsComponent } from './views/school/settings/settings.component';
import { AcademicYearComponent } from './views/school/tasks/academic-year/academic-year.component';
import { ListArchiveLessonsComponent } from './views/school/workspaces/list-archive-lessons/list-archive-lessons.component';
import { OverviewGradeComponent } from './views/school/workspaces/overview.grade/overview.grade.component';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthComponent,
    children: [
      { path: 'login', component: LoginComponent },
      { path: 'forgot-password', component: LoginComponent },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: 'links',
    component: LinkComponent,
    children: [
      { path: 'groupinvite', component: GroupInviteComponent },
      { path: 'eventinvite', component: EventInviteComponent },
      { path: 'orginvite', component: OrgInviteComponent },
    ],
  },
  // Invite links sent before the /links prefix was used in share messages.
  { path: 'groupinvite', redirectTo: 'links/groupinvite' },
  { path: '', redirectTo: 'groups', pathMatch: 'full' },
  {
    path: '',
    component: AdminComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'groups',
        component: GroupComponent,
        children: [
          {
            path: ':id/members',
            title: 'Members',
            component: ListMemberComponent,
          },
          {
            path: ':id/members/create',
            title: 'Create Member',
            component: CreateMemberComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/members/:memberId',
            title: 'Member Details',
            component: DetailMemberComponent,
          },
          {
            path: ':id/members/:memberId/edit',
            title: 'Edit Member Details',
            component: EditMemberComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/prayers',
            title: 'Prayers',
            component: ListPrayerComponent,
          },
          {
            path: ':id/prayers/create',
            title: 'Prayer Create',
            component: CreatePrayerComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/prayers/batch',
            title: 'Prayers Upload',
            component: LoadPrayerComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/prayers/:prayerId',
            title: 'Prayer Details',
            component: DetailPrayerComponent,
          },
          {
            path: ':id/prayers/:prayerId/edit',
            title: 'Prayer Edit',
            component: EditPrayerComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/events',
            title: 'Events',
            component: ListEventComponent,
          },
          CAMPAIGN_ROUTE,
          {
            path: ':id/import',
            title: 'Import People',
            canActivate: [groupAdminGuard],
            loadComponent: () => import('./views/group/import/import-members.component').then((m) => m.ImportMembersComponent),
          },
          {
            path: ':id/events/create',
            title: 'Event Create',
            component: CreateEventComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: ':id/events/:eventId',
            title: 'Event Details',
            component: DetailEventComponent,
          },
          {
            path: ':id/events/:eventId/edit',
            title: 'Event Edit',
            component: EditEventComponent,
            canActivate: [groupWriteAccessGuard],
          },
          {
            path: 'create',
            title: 'Group Create',
            component: CreateGroupComponent,
          },
          {
            path: 'search',
            title: 'Search Communities',
            component: SearchCommunitiesComponent,
          },
          { path: ':id', title: 'Group Home', component: HomeComponent },
          {
            path: ':id/edit',
            title: 'Group Home',
            canActivate: [groupAdminGuard],
            component: EditGroupComponent,
          },
          {
            path: ':id/giving',
            title: 'My Giving',
            component: GivingComponent,
          },
          {
            path: ':id/funding',
            title: 'Funding Requests',
            component: FundingComponent,
          },
          {
            path: ':id/accounting',
            title: 'Accounts',
            canActivate: [groupAccountingGuard],
            data: {
              roles: ['president', 'principal', 'treasurer', 'admin'],
            },
            component: AccountingComponent,
          },
          {
            path: ':id/accounting/upload-receipts',
            title: 'Upload Receipts',
            component: UploadReceiptsComponent,
          },
          {
            path: ':id/affiliation',
            title: 'Governance & Affiliation',
            component: ChurchAffiliationComponent,
          },
          {
            path: ':id/noticeboard',
            title: 'Noticeboard',
            component: NoticeboardComponent,
          },
          {
            path: ':id/noticeboard/create',
            title: 'Create Notice',
            canActivate: [groupAdminGuard],
            component: UpsertNoticeComponent,
          },
          {
            path: ':id/noticeboard/edit/:noticeId',
            title: 'Edit Notice',
            canActivate: [groupAdminGuard],
            component: UpsertNoticeComponent,
          },
          {
            path: ':id/noticeboard/:noticeId',
            title: 'Notice Details',
            component: DlgViewNoticeComponent,
          },

          {
            path: ':id/albums',
            children: [
              {
                path: '',
                title: 'Albums',
                component: ListAlbumsComponent,
              },
              {
                path: ':albumId',
                title: 'Album Details',
                component: ViewAlbumComponent,
              },
            ],
          },
          {
            path: '',
            title: 'Home',
            component: ListGroupsComponent,
          },
        ],
      },
      {
        path: 'school',
        component: GroupComponent,
        providers: [
          provideEffects(
            SchoolMemberEffects,
            TeacherEffects,
            SchoolSessionEffects,
            SchoolGradeEffects,
            SchoolLessonEffects,
            SchoolGradeLessonEffects,
            SchoolAttendanceEffects,
            SchoolAssignmentEffects,
            SchoolExamEffects,
            SchoolAwardEffects,
          ),
          SchoolMemberService,
          SchoolSessionService,
          SchoolGradeService,
          SchoolLessonService,
          SchoolGradeLessonService,
          SchoolAttendanceService,
          SchoolAssignmentService,
          SchoolExamService,
          SchoolAwardService,
        ],
        children: [
          {
            path: ':id/dashboard',
            title: 'Dashboard',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal'] },
            component: DashboardComponent,
          },
          {
            path: ':id/noticeboard',
            title: 'Noticeboard',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: NoticeboardComponent,
          },
          CAMPAIGN_ROUTE,
          {
            path: ':id/calendar',
            title: 'Events Calendar',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: EventsCalendarComponent,
          },
          {
            path: ':id/noticeboard/create',
            title: 'Create Notice',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: UpsertNoticeComponent,
          },
          {
            path: ':id/noticeboard/edit/:noticeId',
            title: 'Edit Notice',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: UpsertNoticeComponent,
          },
          {
            path: ':id/noticeboard/:noticeId',
            title: 'Notice Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DlgViewNoticeComponent,
          },
          {
            path: ':id/quizzes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            children: [
              {
                path: '',
                title: 'Quizzes Home',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: QuizHomeComponent,
              },
              {
                path: 'addQuestions',
                title: 'Add Question(s)',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
                component: CreateQuestionsComponent,
              },
              {
                path: 'questionbank',
                title: 'Question Bank',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
                component: QuestionBankComponent,
              },
              {
                path: 'scheduleQuiz',
                title: 'Create Scheduled Quiz',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: ScheduleQuizComponent,
              },
              {
                path: ':quizId/active',
                title: 'Active Quiz',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: ActiveQuizComponent,
              },
              {
                path: 'createLiveQuiz',
                title: 'Create Live Quiz',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
                component: CreateQuizComponent,
              },
              {
                path: ':quizId/live',
                title: 'Quiz Room',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
                component: LiveQuizRoomComponent,
              },
              {
                path: ':quizId/participant',
                title: 'Quiz Participation',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: LiveParticipantQuizComponent,
              },
              {
                path: ':quizId/report',
                title: 'Quiz Report',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: QuizReportComponent,
              },
              {
                path: ':quizId/edit',
                title: 'Edit Quiz',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
                component: QuizEditComponent,
              },
              {
                path: ':quizId',
                title: 'Quiz Details',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: QuizDetailComponent,
                children: [
                  {
                    path: 'details',
                    component: QuizInfoComponent,
                  },
                  {
                    path: 'analytics',
                    component: QuizAnalyticsComponent,
                    canActivate: [schoolAccessGuard],
                    data: { roles: ['president', 'principal', 'teacher'] },
                  },
                  { path: '', redirectTo: 'details', pathMatch: 'full' },
                ],
              },
            ],
          },
          {
            path: ':id/albums',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            children: [
              {
                path: '',
                title: 'Albums',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: ListAlbumsComponent,
              },
              {
                path: ':albumId',
                title: 'Album Details',
                canActivate: [schoolAccessGuard],
                data: {
                  roles: [
                    'president',
                    'principal',
                    'teacher',
                    'parent',
                    'student',
                  ],
                },
                component: ViewAlbumComponent,
              },
            ],
          },
          {
            path: ':id/members',
            title: 'Members',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal'] },
            component: ListSchoolMembersComponent,
          },
          {
            path: ':id/faculty',
            title: 'Faculty',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher', 'parent'] },
            component: ListFacultyComponent,
          },
          {
            path: ':id/faculty/:facultyId',
            title: 'Faculty Details',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher', 'parent'] },
            component: DetailFacultyComponent,
            children: [
              {
                path: 'sessions',
                component: ListSessionsComponent,
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
              },
              {
                path: 'lessons',
                component: ListGradeLessonsComponent,
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
              },
              {
                path: 'assignments',
                component: ListAssignmentsComponent,
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
              },
              {
                path: 'overview',
                component: OverviewGradeComponent,
                data: {
                  roles: ['president', 'principal', 'teacher'],
                },
              },
              {
                path: 'archive',
                component: ListArchiveLessonsComponent,
                data: { roles: ['president', 'principal', 'teacher'] },
              },
              { path: '', redirectTo: 'sessions', pathMatch: 'full' },
            ],
          },
          {
            path: ':id/faculty/:facultyId/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            component: ViewLessonComponent,
          },
          {
            path: ':id/faculty/:facultyId/lessons/:lessonId/addnotes',
            title: 'Add Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            component: CreateNotesComponent,
          },
          {
            path: ':id/faculty/:facultyId/lessons/:lessonId/editnotes/:noteId',
            title: 'Edit Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            component: EditNotesComponent,
          },
          {
            path: ':id/faculty/:facultyId/lessons/:lessonId/notes/:noteId',
            title: 'Teacher Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewNotesComponent,
          },
          {
            path: ':id/faculty/:facultyId/assignments/create',
            title: 'Create Assignment',
            canActivate: [schoolAccessGuard, groupWriteAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: AddAssignmentComponent,
          },
          {
            path: ':id/faculty/:facultyId/assignments/:assignmentId',
            title: 'Assignment Details',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: DetailAssignmentComponent,
          },
          {
            path: ':id/faculty/:facultyId/assignments/:assignmentId/edit',
            title: 'Edit Assignment',
            canActivate: [schoolAccessGuard, groupWriteAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: EditAssignmentComponent,
          },
          {
            path: ':id/faculty/:facultyId/assignments/:assignmentId/editnotes',
            title: 'Edit Assignment Notes',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: EditAssignmentNotesComponent,
          },
          {
            path: ':id/faculty/:facultyId/students/:studentId',
            title: 'Student Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailStudentComponent,
          },
          {
            path: ':id/faculty/:facultyId/students/:studentId/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewLessonComponent,
          },
          {
            path: ':id/faculty/:facultyId/students/:studentId/lessons/:lessonId/notes/:noteId',
            title: 'Teacher Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewNotesComponent,
          },
          {
            path: ':id/parents/:parentId',
            title: 'My Children',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'parent'] },
            component: DetailParentComponent,
          },
          {
            path: ':id/parents/:parentId/students/:studentId',
            title: 'Child Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailStudentComponent,
          },
          {
            path: ':id/parents/:parentId/students/:studentId/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewLessonComponent,
          },
          {
            path: ':id/parents/:parentId/students/:studentId/lessons/:lessonId/notes/:noteId',
            title: 'Teacher Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewNotesComponent,
          },
          {
            path: ':id/parents/:parentId/students/:studentId/assignments/:assignmentId',
            title: 'Assignment Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailAssignmentComponent,
          },
          // {
          //   path: ':id/parents/:parentId/students/:studentId/awards/:awardId',
          //   title: 'Award Details',
          //   canActivate: [schoolAccessGuard],
          //   data: {
          //     roles: ['president', 'principal', 'teacher', 'parent', 'student'],
          //   },
          //   component: ViewAwardComponent,
          // },
          {
            path: ':id/parents/:parentId/students/:studentId/profile',
            title: 'Edit Student Profile',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['parent'],
            },
            component: EditChildProfileComponent,
          },
          {
            path: ':id/faculty/:facultyId/edit',
            title: 'Edit Faculty Details',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: EditMemberComponent,
          },
          {
            path: ':id/students',
            title: 'Students',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal'] },
            component: ListStudentsComponent,
          },
          {
            path: ':id/students/:studentId',
            title: 'Edit Student Profile',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailStudentComponent,
          },
          {
            path: ':id/students/:studentId/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewLessonComponent,
          },
          {
            path: ':id/students/:studentId/lessons/:lessonId/notes/:noteId',
            title: 'Teacher Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewNotesComponent,
          },
          {
            path: ':id/students/:studentId/assignments/:assignmentId',
            title: 'Assignment Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailAssignmentComponent,
          },
          {
            path: ':id/students/:studentId/edit',
            title: 'Edit Student Details',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: EditMemberComponent,
          },
          {
            path: ':id/new',
            title: 'New Members',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal'] },
            component: ListUnassignedComponent,
          },
          {
            path: ':id/grades',
            title: 'Grades',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: ListGradesComponent,
          },
          {
            path: ':id/grades/:gradeId',
            title: 'Grade Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailGradeComponent,
          },
          {
            path: ':id/grades/:gradeId/sessions/:sessionId',
            title: 'Session Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailSessionComponent,
          },
          {
            path: ':id/grades/:gradeId/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewLessonComponent,
          },
          {
            path: ':id/grades/:gradeId/assignments/:assignmentId',
            title: 'Assignment Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailAssignmentComponent,
          },
          {
            path: ':id/grades/:gradeId/lessons/:lessonId/notes/:noteId',
            title: 'Teacher Notes',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ViewNotesComponent,
          },
          {
            path: ':id/lessons',
            title: 'All Lessons',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            children: [
              {
                path: '',
                title: 'List Lesson',
                component: ListLessonsComponent,
              },
              {
                path: 'create',
                title: 'Create Lesson',
                component: AddLessonComponent,
                canActivate: [groupWriteAccessGuard],
              },
              {
                path: ':lessonId/edit',
                title: 'Edit Lesson',
                component: UpdateLessonComponent,
                canActivate: [groupWriteAccessGuard],
              },
              {
                path: ':lessonId',
                title: 'Lesson Details',
                component: DetailLessonComponent,
              },
            ],
          },
          {
            path: ':id/lessons/:lessonId',
            title: 'Lesson Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            component: DetailLessonComponent,
          },
          {
            path: ':id/attendance',
            title: 'Attendance',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: ListAttendanceComponent,
          },
          {
            path: ':id/attendance/create',
            title: 'Attendance Create',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher'],
            },
            component: CreateAttendanceComponent,
          },
          {
            path: ':id/attendance/:attendanceId',
            title: 'Attendance Details',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal', 'teacher', 'parent', 'student'],
            },
            component: DetailAttendanceComponent,
          },
          {
            path: ':id/attendance/:attendanceId/edit',
            title: 'Attendance Edit',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal', 'teacher'] },
            component: EditAttendanceComponent,
          },
          {
            path: ':id/details',
            title: 'School Settings',
            component: SchoolSettingsComponent,
          },
          {
            path: ':id/billing',
            title: 'School Billing',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal'],
            },
            children: [
              {
                path: 'addonsubscribe',
                title: 'Add-On Subscriptions',
                component: SubscriptionComponent,
              },
              {
                path: 'transactions',
                title: 'AI Credit Transactions',
                component: CreditTransactionsComponent,
              },
              {
                path: '',
                title: 'Billing Details',
                component: ViewAccountComponent,
              },
            ],
          },
          {
            path: ':id/details/newacademicyear',
            title: 'New Academic Year',
            canActivate: [schoolAccessGuard],
            data: { roles: ['president', 'principal'] },
            component: AcademicYearComponent,
          },
          {
            path: ':id/details/edit',
            title: 'School Details Edit',
            canActivate: [groupAdminGuard],
            component: EditGroupComponent,
          },
          {
            path: ':id/giving',
            title: 'My Giving',
            component: GivingComponent,
          },
          {
            path: ':id/funding',
            title: 'Funding Requests',
            component: FundingComponent,
          },
          {
            path: ':id/accounting',
            title: 'Accounts',
            canActivate: [groupAccountingGuard],
            data: {
              roles: ['president', 'principal', 'treasurer', 'admin'],
            },
            component: AccountingComponent,
          },
          {
            path: ':id/accounting/upload-receipts',
            title: 'Upload Receipts',
            component: UploadReceiptsComponent,
          },
          {
            path: ':id/import',
            title: 'Import People',
            canActivate: [schoolAccessGuard],
            data: {
              roles: ['president', 'principal'],
            },
            loadComponent: () => import('./views/group/import/import-members.component').then((m) => m.ImportMembersComponent),
          },
          {
            path: ':id',
            redirectTo: ':id/dashboard',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'channel',
        component: ChannelComponent,
        canActivate: [authGuard],
        children: [
          { path: '', title: 'Channel Home', component: ViewChannelComponent },
          {
            path: 'create',
            title: 'Channel Create',
            component: CreateChannelComponent,
          },
          {
            path: 'edit',
            title: 'Channel Edit',
            component: EditChannelComponent,
          },
        ],
      },
      {
        path: 'videos',
        component: MyVideosComponent,
        canActivate: [authGuard],
      },
      {
        path: 'videos/create',
        title: 'Video Create',
        component: CreateVideoComponent,
      },
      {
        path: 'videos/:id',
        title: 'Video Details',
        component: MyVideoComponent,
      },
      {
        path: 'videos/:id/edit',
        title: 'Video Edit',
        component: EditVideoComponent,
      },

      {
        path: 'podcasts',
        component: MyVideosComponent,
        canActivate: [authGuard],
      },
      {
        path: 'podcasts/create',
        title: 'Podcast Create',
        component: CreateVideoComponent,
      },
      {
        path: 'podcasts/:id',
        title: 'Podcast Details',
        component: MyVideoComponent,
      },
      {
        path: 'podcasts/:id/edit',
        title: 'Podcast Edit',
        component: EditVideoComponent,
      },
      {
        path: 'articles',
        component: MyArticlesComponent,
        canActivate: [authGuard],
      },
      {
        path: 'articles/create',
        title: 'Article Create',
        canActivate: [authGuard],
        component: CreateArticleComponent,
      },
      {
        path: 'articles/:id',
        title: 'Article Details',
        canActivate: [authGuard],
        component: MyArticleComponent,
      },
      {
        path: 'articles/:id/edit',
        title: 'Article Edit',
        canActivate: [authGuard],
        component: EditArticleComponent,
      },
      {
        // Request to create an organization (approved by an AtChurch platform admin)
        path: 'organizations/request',
        title: 'Create Organization',
        canActivate: [authGuard],
        component: OrgRequestComponent,
      },
      {
        // Platform admins review organization requests (also enforced by the platformAdmin claim in rules)
        path: 'platform/organization-requests',
        title: 'Organization Requests',
        canActivate: [authGuard],
        component: OrgRequestsReviewComponent,
      },
      {
        // Join link / QR code an organization shares with churches (not under /links:
        // the mobile app claims /links/* and has no screen for this)
        path: 'organizations/:orgId/join',
        title: 'Join Organization',
        canActivate: [authGuard],
        component: JoinOrganizationComponent,
      },
      {
        path: 'management',
        canActivate: [authGuard],
        loadChildren: () =>
          import('./views/management/management.routes').then(
            (m) => m.MANAGEMENT_ROUTES
          ),
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [authGuard],
        children: [
          { path: '', redirectTo: 'details', pathMatch: 'full' },
          {
            path: 'details',
            title: 'Profile Details',
            component: ViewProfileComponent,
          },
          {
            path: 'settings',
            title: 'Settings',
            component: ProfileSettingsComponent,
          },
          {
            path: 'giving',
            title: 'My Giving',
            component: MyGivingComponent,
          },
          {
            path: 'certificates',
            title: 'My Certificates',
            component: MyCertificatesComponent,
          },
          {
            path: 'edit',
            title: 'Edit Profile',
            component: EditProfileComponent,
          },
        ],
      },
      {
        path: 'billing/success',
        title: 'Billing Success',
        component: ResultComponent,
      },
      { path: 'howto', title: 'How To Guides', component: HowToComponent },
      { path: '**', pathMatch: 'full', component: NotFoundComponent },
    ],
  },
  // { path: '**', redirectTo: 'groups', pathMatch: 'full' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
